import { useParams } from 'react-router-dom'
import { Printer } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion'
import { Skeleton } from '@/components/ui/Skeleton'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { formatDateTime, formatNaira } from '@/lib/format'
import { useTransaction } from './hooks'
import { ChannelBadge } from './components/ChannelBadge'
import { StatusChip } from './components/StatusChip'
import { SourceDestinationPanel } from './components/SourceDestinationPanel'
import { NameEnquiryBadge } from './components/NameEnquiryBadge'
import { MccBadge } from './components/MccBadge'
import { EntryModeIcon } from './components/EntryModeIcon'
import { CopyableField } from './components/CopyableField'
import { BalancePanel } from './components/BalancePanel'
import { LinksPanel } from './components/LinksPanel'
import { RawPayloadPanel } from './components/RawPayloadPanel'

/**
 * AF-125 — Transaction detail screen.
 *
 * Composes seven sections per the AC: Overview, Source, Destination,
 * Channel & Device, Merchant/Terminal, Balances, Raw Payload — plus a
 * Linked Entities section for fast pivots to alerts/cases/decisions.
 *
 * Layout:
 *   - Sticky header with Status + Channel + amount + print button.
 *   - Section list rendered as Radix Accordion with all items open by
 *     default. On narrow viewports the user can collapse to skim; on print
 *     the global CSS forces every item open regardless of state.
 *
 * Responsive notes:
 *   - Source / Destination tiles stack on mobile, side-by-side on ≥ 768px.
 *   - The header chips wrap on narrow viewports; nothing horizontally scrolls.
 */
export function TransactionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, isError, error, refetch } = useTransaction(id)

  if (isLoading) return <DetailSkeleton />
  if (isError) return <DetailError message={error.message} onRetry={() => void refetch()} />
  if (!data) return null

  const t = data
  const sections: ReadonlyArray<readonly [string, string, React.ReactNode]> = [
    ['overview', 'Overview', <OverviewSection key="o" txn={t} />],
    [
      'source',
      'Source',
      <div key="s" className="grid gap-4 md:grid-cols-2">
        <SourceDestinationPanel side={t.source} title="Source" />
        <SourceDestinationPanel side={t.destination} title="Destination" />
      </div>,
    ],
    [
      'channel',
      'Channel & Device',
      <ChannelDeviceSection key="c" txn={t} />,
    ],
    [
      'merchant',
      'Merchant / Terminal',
      <MerchantSection key="m" txn={t} />,
    ],
    [
      'balances',
      'Balances',
      <BalancePanel key="b" balances={t.balances} />,
    ],
    [
      'links',
      'Linked entities',
      <LinksPanel
        key="l"
        alerts={t.linked_alerts}
        cases={t.linked_cases}
        decisions={t.linked_decisions}
      />,
    ],
    [
      'raw',
      'Raw payload',
      <RawPayloadPanel key="r" payload={t.raw_payload} />,
    ],
  ]

  return (
    <article className="flex flex-col gap-4" data-print-section="true">
      <header className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold">{t.transaction_ref}</h1>
            <StatusChip status={t.status} />
            <ChannelBadge channel={t.channel} />
          </div>
          <div className="text-sm text-muted-fg">
            {formatDateTime(t.occurred_at)}
            {t.posted_at && (
              <>
                {' · posted '}
                {formatDateTime(t.posted_at)}
              </>
            )}
          </div>
          {t.description && (
            <p className="text-sm text-fg">{t.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-muted-fg">Amount</div>
            <div className="font-mono text-2xl font-semibold">
              {formatNaira(t.amount_minor)}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            data-print-hidden="true"
            aria-label="Print transaction for evidence export"
          >
            <Printer size={14} />
            Print
          </Button>
        </div>
      </header>

      <Accordion
        type="multiple"
        defaultValue={sections.map(([k]) => k)}
        className="flex flex-col gap-2"
      >
        {sections.map(([key, label, body]) => (
          <AccordionItem
            key={key}
            value={key}
            className="rounded-lg border border-border bg-card"
            data-print-section="true"
          >
            <AccordionTrigger className="px-4">{label}</AccordionTrigger>
            <AccordionContent className="px-4">{body}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </article>
  )
}

function OverviewSection({ txn }: { txn: ReturnType<typeof useTransaction>['data'] }) {
  if (!txn) return null
  // The header already shows ref / status / channel / amount / time; the
  // Overview section adds the analyst-relevant signal fields that don't
  // belong in the header but are too important to live deep in a sub-section.
  return (
    <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
      <Row label="Name enquiry">
        <NameEnquiryBadge value={txn.name_enquiry} />
      </Row>
      {txn.nip_session_id && (
        <Row label="NIP session ID">
          <CopyableField
            value={txn.nip_session_id}
            copyLabel="Copy NIP session ID"
            toastMessage="NIP session ID copied"
          />
        </Row>
      )}
      {txn.mcc && (
        <Row label="Merchant category">
          <MccBadge mcc={txn.mcc} />
        </Row>
      )}
      {txn.entry_mode && (
        <Row label="Entry mode">
          <EntryModeIcon mode={txn.entry_mode} />
        </Row>
      )}
    </dl>
  )
}

function ChannelDeviceSection({ txn }: { txn: ReturnType<typeof useTransaction>['data'] }) {
  if (!txn) return null
  const hasDevice =
    txn.device !== null &&
    (txn.device.device_model !== null ||
      txn.device.os !== null ||
      txn.device.ip_address !== null)
  if (!hasDevice) {
    return <p className="text-sm text-muted-fg">No additional channel or device metadata.</p>
  }
  return (
    <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
      {txn.device?.device_model && <Row label="Device">{txn.device.device_model}</Row>}
      {txn.device?.os && <Row label="OS">{txn.device.os}</Row>}
      {txn.device?.ip_address && (
        <Row label="IP address">
          <CopyableField value={txn.device.ip_address} mono={false} />
        </Row>
      )}
    </dl>
  )
}

function MerchantSection({ txn }: { txn: ReturnType<typeof useTransaction>['data'] }) {
  if (!txn) return null
  if (!txn.terminal && !txn.mcc) {
    return <p className="text-sm text-muted-fg">No merchant or terminal data on this transaction.</p>
  }
  return (
    <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
      {txn.terminal && (
        <>
          <Row label="Terminal ID">
            <CopyableField value={txn.terminal.terminal_id} />
          </Row>
          <Row label="Merchant">
            <span className="text-sm">{txn.terminal.merchant_name}</span>
          </Row>
          {txn.terminal.location && <Row label="Location">{txn.terminal.location}</Row>}
          {txn.terminal.city && (
            <Row label="City">
              {txn.terminal.city}
              {txn.terminal.country && (
                <Badge variant="default" className="ml-2">
                  {txn.terminal.country}
                </Badge>
              )}
            </Row>
          )}
        </>
      )}
      {txn.mcc && (
        <Row label="MCC">
          <MccBadge mcc={txn.mcc} />
        </Row>
      )}
    </dl>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border/50 py-1.5 last:border-b-0">
      <dt className="text-xs uppercase tracking-wide text-muted-fg">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-9 w-32" />
      </div>
      <Skeleton className="h-64" />
    </div>
  )
}

function DetailError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Couldn't load transaction</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-fg">{message}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          Retry
        </Button>
      </CardContent>
    </Card>
  )
}
